import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BriTeammateAuthorizationService } from '@davita/bridge-utility-library/authorization';

/**
 * Interceptor to handle authentication errors (400/401).
 *
 * When a token expires, the backend returns 400 or 401 errors.
 * This interceptor catches those and forces a re-login.
 */
@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  constructor(private authService: BriTeammateAuthorizationService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check for authentication errors
        if (this.isAuthError(error)) {
          console.warn('Authentication error detected, forcing re-login:', error.status);
          this.forceRelogin();
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Determine if the error is an authentication/token error.
   *
   * Common scenarios:
   * - 401 Unauthorized: Token is invalid or expired
   * - 400 Bad Request: Can indicate token issues with some auth systems
   */
  private isAuthError(error: HttpErrorResponse): boolean {
    // Always treat 401 as auth error
    if (error.status === 401) {
      return true;
    }

    // For 400 errors, check if it's auth-related
    if (error.status === 400) {
      const errorBody = error.error;

      // Check for common auth error indicators in the response
      if (typeof errorBody === 'string') {
        const lowerBody = errorBody.toLowerCase();
        return lowerBody.includes('token') ||
               lowerBody.includes('auth') ||
               lowerBody.includes('expired') ||
               lowerBody.includes('invalid');
      }

      if (typeof errorBody === 'object' && errorBody !== null) {
        const errorMessage = errorBody.message || errorBody.error || '';
        const lowerMessage = errorMessage.toLowerCase();
        return lowerMessage.includes('token') ||
               lowerMessage.includes('auth') ||
               lowerMessage.includes('expired') ||
               lowerMessage.includes('invalid');
      }
    }

    return false;
  }

  /**
   * Force user to re-login by clearing auth state and redirecting.
   */
  private forceRelogin(): void {
    // Clear any stored auth data
    sessionStorage.clear();
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');

    // Redirect to login - the Bridge library should handle the OAuth flow
    window.location.href = '/';
  }
}
